// src/components/AddRecipientModal.jsx
import React, { useState, useEffect } from 'react';
import { createRecipient, updateRecipient } from '../services/api';
import './AddRecipientModal.css';

function AddRecipientModal({ recipient, onClose, onSuccess }) {
  const isEditing = !!recipient;

  const [formData, setFormData] = useState({
    recipientName: '',
    recipientPhone: '',
    recipientCountry: 'KE',
    recipientType: 'mobile_money',
    relationship: '',
    nickname: '',
    isFavorite: false,
    bankName: '',
    bankAccountNumber: '',
    bankBranch: '',
    bankCode: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (recipient) {
      setFormData({
        recipientName: recipient.recipientName || '',
        recipientPhone: recipient.recipientPhone || '',
        recipientCountry: recipient.recipientCountry || 'KE',
        recipientType: recipient.recipientType || 'mobile_money',
        relationship: recipient.relationship || '',
        nickname: recipient.nickname || '',
        isFavorite: recipient.isFavorite || false,
        bankName: recipient.bankName || '',
        bankAccountNumber: recipient.bankAccountNumber || '',
        bankBranch: recipient.bankBranch || '',
        bankCode: recipient.bankCode || '',
      });
    }
  }, [recipient]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.recipientName.trim()) {
      newErrors.recipientName = 'Name is required';
    }

    if (!formData.recipientPhone.trim()) {
      newErrors.recipientPhone = 'Phone number is required';
    } else {
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      if (!phoneRegex.test(formData.recipientPhone.replace(/[\s-]/g, ''))) {
        newErrors.recipientPhone = 'Invalid phone number format';
      }
    }

    if (!formData.recipientCountry) {
      newErrors.recipientCountry = 'Country is required';
    }

    if (!formData.recipientType) {
      newErrors.recipientType = 'Recipient type is required';
    }

    if (formData.recipientType === 'bank_account') {
      if (!formData.bankName.trim()) {
        newErrors.bankName = 'Bank name is required';
      }
      if (!formData.bankAccountNumber.trim()) {
        newErrors.bankAccountNumber = 'Account number is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const dataToSend = { ...formData };
      if (formData.recipientType !== 'bank_account') {
        delete dataToSend.bankName;
        delete dataToSend.bankAccountNumber;
        delete dataToSend.bankBranch;
        delete dataToSend.bankCode;
      }

      const response = isEditing
        ? await updateRecipient(recipient.id, dataToSend)
        : await createRecipient(dataToSend);

      if (response.success) {
        onSuccess(
          isEditing
            ? 'Recipient updated successfully'
            : 'Recipient added successfully'
        );
      } else {
        setError(response.message || 'Failed to save recipient');
      }
    } catch (err) {
      setError(err.message || 'Failed to save recipient');
    } finally {
      setLoading(false);
    }
  };

  const getCountryOptions = () => [
    { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹' },
  ];

  const getKenyanBanks = () => [
    { name: 'Equity Bank', code: '68000' },
    { name: 'KCB Bank', code: '01000' },
    { name: 'Cooperative Bank', code: '11000' },
    { name: 'NCBA Bank', code: '07000' },
    { name: 'Absa Bank', code: '03000' },
    { name: 'Standard Chartered', code: '02000' },
    { name: 'DTB Bank', code: '63000' },
    { name: 'I&M Bank', code: '57000' },
    { name: 'Stanbic Bank', code: '31000' },
    { name: 'Family Bank', code: '70000' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal add-recipient-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEditing ? 'Edit Recipient' : 'Add New Recipient'}</h3>
          <button className="close-btn" onClick={onClose} disabled={loading}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="alert alert-error">
                <span className="icon">⚠</span>
                {error}
              </div>
            )}

            <div className="form-section">
              <h4>Basic Information</h4>

              <div className="form-group">
                <label htmlFor="recipientName">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="recipientName"
                  name="recipientName"
                  value={formData.recipientName}
                  onChange={handleChange}
                  placeholder="e.g., Jane Wanjiku"
                  className={errors.recipientName ? 'error' : ''}
                  disabled={loading}
                />
                {errors.recipientName && (
                  <span className="error-text">{errors.recipientName}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recipientPhone">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="recipientPhone"
                    name="recipientPhone"
                    value={formData.recipientPhone}
                    onChange={handleChange}
                    placeholder="+254712345678"
                    className={errors.recipientPhone ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.recipientPhone && (
                    <span className="error-text">{errors.recipientPhone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="recipientCountry">
                    Country <span className="required">*</span>
                  </label>
                  <select
                    id="recipientCountry"
                    name="recipientCountry"
                    value={formData.recipientCountry}
                    onChange={handleChange}
                    className={errors.recipientCountry ? 'error' : ''}
                    disabled={loading}
                  >
                    {getCountryOptions().map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.recipientCountry && (
                    <span className="error-text">{errors.recipientCountry}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="recipientType">
                  Recipient Type <span className="required">*</span>
                </label>
                <select
                  id="recipientType"
                  name="recipientType"
                  value={formData.recipientType}
                  onChange={handleChange}
                  className={errors.recipientType ? 'error' : ''}
                  disabled={loading}
                >
                  <option value="mobile_money">Mobile Money (M-Pesa)</option>
                  <option value="bank_account">Bank Account</option>
                  <option value="cash_pickup">Cash Pickup</option>
                </select>
                {errors.recipientType && (
                  <span className="error-text">{errors.recipientType}</span>
                )}
              </div>
            </div>

            {formData.recipientType === 'bank_account' && (
              <div className="form-section">
                <h4>Bank Account Details</h4>

                <div className="form-group">
                  <label htmlFor="bankName">
                    Bank Name <span className="required">*</span>
                  </label>
                  <select
                    id="bankName"
                    name="bankName"
                    value={formData.bankName}
                    onChange={(e) => {
                      const bank = getKenyanBanks().find(b => b.name === e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        bankName: e.target.value,
                        bankCode: bank ? bank.code : '',
                      }));
                      if (errors.bankName) {
                        setErrors(prev => ({ ...prev, bankName: '' }));
                      }
                    }}
                    className={errors.bankName ? 'error' : ''}
                    disabled={loading}
                  >
                    <option value="">Select a bank...</option>
                    {getKenyanBanks().map((bank) => (
                      <option key={bank.code} value={bank.name}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                  {errors.bankName && (
                    <span className="error-text">{errors.bankName}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bankAccountNumber">
                      Account Number <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="bankAccountNumber"
                      name="bankAccountNumber"
                      value={formData.bankAccountNumber}
                      onChange={handleChange}
                      placeholder="1234567890"
                      className={errors.bankAccountNumber ? 'error' : ''}
                      disabled={loading}
                    />
                    {errors.bankAccountNumber && (
                      <span className="error-text">{errors.bankAccountNumber}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="bankBranch">Branch (Optional)</label>
                    <input
                      type="text"
                      id="bankBranch"
                      name="bankBranch"
                      value={formData.bankBranch}
                      onChange={handleChange}
                      placeholder="e.g., Nairobi"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-section">
              <h4>Additional Information (Optional)</h4>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nickname">Nickname</label>
                  <input
                    type="text"
                    id="nickname"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleChange}
                    placeholder="e.g., Mom, Brother"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="relationship">Relationship</label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Select...</option>
                    <option value="family">Family</option>
                    <option value="friend">Friend</option>
                    <option value="business">Business</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isFavorite"
                    checked={formData.isFavorite}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span>⭐ Mark as favorite</span>
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Recipient' : 'Add Recipient')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddRecipientModal;